import React, { useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { PaperPlaneTilt, Plus, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPORT_FILES = [
  { key: 'sales', label: 'Journal des ventes' },
  { key: 'purchases', label: 'Journal des achats' },
  { key: 'cash', label: 'Journal de caisse' },
  { key: 'bank', label: 'Journal de banque' },
  { key: 'ledger', label: 'Écritures comptables (partie double)' },
  { key: 'fec', label: 'FEC (norme fiscale)' },
];

const AccountingEmailSettings = ({ settings, setSettings }) => {
  const [newEmail, setNewEmail] = useState('');
  const [sending, setSending] = useState(false);
  const recipients = settings.accounting_recipient_emails || [];
  const selectedFiles = settings.accounting_export_files || [];

  const addEmail = () => {
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Email invalide');
      return;
    }
    if (recipients.includes(trimmed)) {
      toast.error('Email déjà ajouté');
      return;
    }
    setSettings({ ...settings, accounting_recipient_emails: [...recipients, trimmed] });
    setNewEmail('');
  };

  const removeEmail = (email) => {
    setSettings({ ...settings, accounting_recipient_emails: recipients.filter(e => e !== email) });
  };

  const toggleFile = (key) => {
    const next = selectedFiles.includes(key)
      ? selectedFiles.filter(k => k !== key)
      : [...selectedFiles, key];
    setSettings({ ...settings, accounting_export_files: next });
  };

  const sendNow = async () => {
    if (recipients.length === 0) {
      toast.error('Ajoutez au moins un destinataire');
      return;
    }
    if (selectedFiles.length === 0) {
      toast.error('Sélectionnez au moins un fichier');
      return;
    }
    setSending(true);
    try {
      // Persist current settings first, then send
      await axios.put(`${API_URL}/api/company-settings`, settings, { withCredentials: true });
      const res = await axios.post(
        `${API_URL}/api/accounting/email-exports`,
        { recipients, file_keys: selectedFiles, fmt: settings.accounting_export_format || 'xlsx' },
        { withCredentials: true }
      );
      toast.success(`Envoyé à ${(res.data.recipients || []).join(', ')}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Envoi des exports comptables par email</h2>
        <p className="text-sm text-slate-600">Configurez l&apos;envoi automatique mensuel ou envoyez les exports à la demande à votre expert-comptable.</p>
      </div>

      {/* Destinataires */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h3 className="font-semibold text-slate-800 mb-3">Destinataires</h3>
        <div className="flex gap-2 mb-3">
          <Input
            type="email"
            placeholder="comptable@cabinet.fr"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
            data-testid="accounting-email-input"
          />
          <Button type="button" onClick={addEmail} data-testid="accounting-email-add-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
            <Plus size={16} className="mr-1" /> Ajouter
          </Button>
        </div>
        {recipients.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Aucun destinataire</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {recipients.map(email => (
              <span key={email} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 border border-blue-200 text-blue-800 rounded-full text-sm">
                {email}
                <button
                  type="button"
                  onClick={() => removeEmail(email)}
                  data-testid={`remove-email-${email}`}
                  className="hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Fichiers à inclure */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h3 className="font-semibold text-slate-800 mb-3">Fichiers à inclure</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {EXPORT_FILES.map(f => (
            <label key={f.key} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={selectedFiles.includes(f.key)}
                onChange={() => toggleFile(f.key)}
                data-testid={`accounting-file-${f.key}`}
              />
              <span className="text-sm text-slate-700">{f.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Format */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h3 className="font-semibold text-slate-800 mb-3">Format des fichiers</h3>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={settings.accounting_export_format === 'xlsx'}
              onChange={() => setSettings({ ...settings, accounting_export_format: 'xlsx' })}
              data-testid="accounting-format-xlsx"
            />
            <span className="text-sm">Excel (.xlsx)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={settings.accounting_export_format === 'csv'}
              onChange={() => setSettings({ ...settings, accounting_export_format: 'csv' })}
              data-testid="accounting-format-csv"
            />
            <span className="text-sm">CSV</span>
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-2">Le FEC est toujours envoyé au format texte (.txt) conformément à la norme fiscale.</p>
      </div>

      {/* Envoi automatique */}
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
        <h3 className="font-semibold text-slate-800 mb-3">Envoi automatique mensuel</h3>
        <label className="flex items-center gap-2 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={!!settings.accounting_auto_send}
            onChange={(e) => setSettings({ ...settings, accounting_auto_send: e.target.checked })}
            data-testid="accounting-auto-send-toggle"
          />
          <span className="text-sm font-medium">Activer l&apos;envoi automatique chaque mois</span>
        </label>
        {settings.accounting_auto_send && (
          <div className="flex items-center gap-3 ml-6">
            <label className="text-sm text-slate-700">Jour du mois :</label>
            <Input
              type="number"
              min="1"
              max="28"
              value={settings.accounting_auto_send_day || 1}
              onChange={(e) => setSettings({ ...settings, accounting_auto_send_day: Math.max(1, Math.min(28, parseInt(e.target.value) || 1)) })}
              data-testid="accounting-auto-send-day"
              className="w-20"
            />
            <span className="text-xs text-slate-500">à 8h00 (fuseau horaire de la société). Période = mois précédent.</span>
          </div>
        )}
      </div>

      {/* Send manually */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-slate-900 mb-2">Envoi manuel maintenant</h3>
        <p className="text-sm text-slate-700 mb-3">Envoie immédiatement les exports comptables aux destinataires configurés (sans filtre de période).</p>
        <Button
          onClick={sendNow}
          disabled={sending}
          data-testid="send-accounting-email-now"
          className="bg-blue-600 hover:bg-blue-500 text-white"
        >
          <PaperPlaneTilt size={18} className="mr-2" />
          {sending ? 'Envoi en cours...' : 'Envoyer maintenant'}
        </Button>
        <p className="text-xs text-slate-500 mt-2">⚠️ Pensez à enregistrer les paramètres avant l&apos;envoi (bouton « Sauvegarder » en haut). Le bouton « Envoyer maintenant » sauvegarde automatiquement avant l&apos;envoi.</p>
      </div>
    </div>
  );
};

export default AccountingEmailSettings;
